import { createFileRoute } from "@tanstack/react-router";

import { useMemo, useState, useCallback, useRef } from "react";

import { Row } from "@tanstack/react-table";

import { useQueryParams } from "@/hooks/use-query-params";
import {
  useGetData,
  usePostData,
  usePutData,
} from "@/hooks/services/use-query-hooks";
import {
  CreateSubJobOrder,
  DeleteJobOrder,
  JobOrderList,
  UpdateBulkJobOrder,
  UpdateJobOrder,
  UpdateJobOrderReorder,
} from "@/hooks/services/pps/handlers/job-orders-api";
import {
  Job,
  JobOrderReorderPayload,
  JobPayload,
  JobResponse,
  SubJobPayload,
} from "@/hooks/services/pps/types/job-orders.type";

import { Button } from "@/components/ui/button";
import {
  DataTable,
  type ColumnDef,
  type SortAndPagination,
} from "@/components/datatable";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { renderCell } from "@/lib/utils";
import {
  serializeFiltersToQuery,
  serializeSortsToQuery,
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";
import {
  ColumnType,
  DynamicDataTableEditingColumn,
} from "@/components/datatable/dynamic-datatable-editing-column";
import {
  BarChart,
  Calendar,
  ClipboardList,
  FileText,
  Wrench,
} from "lucide-react";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";
import { useDatatable } from "@/stores/components/datatable";
import KanbanDrawerContent from "../components/kanban/kanban-drawer-content";
import {
  defaultSelectedItem,
  jobOrderStatusMap,
  ViewPps,
} from "../utils/constants";
import { useWorkSpaceStore } from "@/stores/store";
import { useColumnInfoStore } from "@/stores/column-info";
import { usePermissions } from "@/hooks/use-permissions";
import { toastManager } from "@/lib/toast-manager";

export const Route = createFileRoute("/(planner-screens)/planner/list")({
  component: RouteComponent,
});

function RouteComponent() {
  const { params } = useQueryParams();
  const { columnsSchema } = useColumnInfoStore();
  const { workspace } = useWorkSpaceStore();
  const permissions = usePermissions(); // WOR-1793: Add permission hook

  const { setRowSelectionTable, multipleRowSelections, table } = useDatatable();
  const tableRef = useRef<any>(null);

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const updateJobOrder = usePutData<JobPayload, any>(UpdateJobOrder());

  const subJobOrder = usePostData(CreateSubJobOrder());
  const reorderJobOrder = usePutData(UpdateJobOrderReorder());
  //
  const technician = useGetData(
    TechnicianList({ workspaceId: workspace?.id || 0 }),
  );

  const { mutate } = usePostData(DeleteJobOrder());
  const [refetchFn, setRefetchFn] = useState<(() => void) | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>({
    show: false,
    task: defaultSelectedItem,
  });

  const filterParams = {
    ...params,
    ...(workspace?.id && { workspaceId: workspace?.id }),
    viewId: ViewPps.list.id,
    ...(dynamicSorts.length > 0 && {
      encodeQuery: {
        sorts: serializeSortsToQuery(
          dynamicSorts.map((sort) => {
            const column = columnsSchema.find(
              (col: any) => col.name === sort.field,
            );
            if (column?.sort?.by && column.sort.active) {
              return {
                ...sort,
                field: column.sort.by,
              };
            }
            return sort;
          }),
        ),
        ...(dynamicFilters.length > 0 && {
          filters: serializeFiltersToQuery(
            dynamicFilters.map((filter) => {
              const column = columnsSchema.find(
                (col: any) => col.name === filter.filterBy,
              );
              if (column?.filter?.by && column.filter.active) {
                return {
                  ...filter,
                  filterBy: column.filter.by,
                };
              }
              return filter;
            }),
          ),
        }),
      },
    }),
    ...(dynamicFilters.length > 0 &&
      dynamicSorts.length === 0 && {
        encodeQuery: {
          filters: serializeFiltersToQuery(
            dynamicFilters.map((filter) => {
              const column = columnsSchema.find(
                (col: any) => col.name === filter.filterBy,
              );
              if (column?.filter?.by && column.filter.active) {
                return {
                  ...filter,
                  filterBy: column.filter.by,
                };
              }
              return filter;
            }),
          ),
        },
      }),
  };

  const columns = useMemo<ColumnDef<Job>[]>(() => {
    return columnsSchema
      .sort((a: any, b: any) => a.position - b.position)
      .map((column: any) => ({
        accessorKey: column.name as keyof Job,
        id: `${column.name}_${column.label.replace(/\s+/g, "_")}`,
        header: column.label,
        cell: ({ row }: { row: Row<Job> }) => renderCell(row, column),
        type: column.type,
        editable: permissions.canManagePlant1()
          ? (column?.update?.active ?? false)
          : false, // WOR-1793 Req #5: Disable cell editing if no Plant1_ManageJob
        attribute: column.update.by || column.name,
        options: column.options || [],
      })) as ColumnDef<Job>[];
  }, [columnsSchema, permissions]);

  const handleRefetch = useCallback((refetchCallback: () => void) => {
    setRefetchFn(() => refetchCallback);
  }, []);

  // Memoized selection change handler to prevent infinite loop
  const handleSelectionChange = useCallback(
    (rows: Row<Job>[]) => {
      const selection: Record<string, boolean> = {};
      rows.forEach((row) => {
        selection[row.id] = true;
      });
      setRowSelectionTable(selection);
    },
    [setRowSelectionTable],
  );

  // Memoized table ready handler to prevent infinite loop
  const handleTableReady = useCallback((tableInstance: any) => {
    if (tableRef.current !== tableInstance) {
      tableRef.current = tableInstance;
      useDatatable.setState({ table: tableInstance });
    }
  }, []);

  const handleAddSubJobOrder = (row: Row<Job>) => {
    const body: SubJobPayload = {
      workspaceId: row.original.workspaceId,
      parentId: Number(row.original.id),
      jobOrderNo: row.original.jobOrderNo,
      costCentre: row.original.costCentre,
      shopId: row.original.shopId,
      statusId: row.original.statusId,
      processId: row.original.processId,
      partId: row.original.partId,
      serialNumber: row.original.serialNumber,
      customerId: row.original.customerId,
      dueDate: row.original.dueDate,
      startDate: row.original.startDate,
      standardHours: row.original.standardHours,
      totalCost: row.original.totalCost,
      totalSales: row.original.totalSales,
      materialEdd: row.original.materialEdd,
      materialStatus: row.original.materialStatus,
      pbsubcontEdd: row.original.pbsubcontEdd,
      pbsubcontStatus: row.original.pbsubcontStatus,
      prodRemarks: row.original.prodRemarks,
      technicianIds: row.original.ppsTechnicians.map((i) => i.id),
    };

    subJobOrder.mutate(body, {
      onSuccess: () => {
        toastManager.showSuccess(
          "Sub Row Added Successfully",
          `Your sub row from ${body.jobOrderNo} has been added successfully.`,
          3000,
        );
        refetchFn?.();
      },
      onError: () => {
        toastManager.showError(
          "Error Adding Sub Row",
          "There was an issue adding the sub row. Please try again or contact support if the problem persists",
        );
      },
    });
  };

  const getRowBy = (rowIndex: number) => {
    return table?.getRowModel().rows.find((_, idx) => idx === rowIndex);
  };

  // Handle row order change from new DataTable
  const handleRowOrderChange = useCallback(
    (oldIndex: number, newIndex: number, pagination: SortAndPagination) => {
      if (dynamicFilters.length !== 0 || dynamicSorts.length !== 0) {
        setIsDialogOpen(true);
        return;
      }

      if (oldIndex === newIndex) {
        return;
      }

      let jobOrderIds: number[] = [];
      let rows;
      let destinationRows;

      if (multipleRowSelections.length > 0) {
        rows = getRowBy(oldIndex);
        destinationRows = getRowBy(newIndex);

        jobOrderIds = [...multipleRowSelections].map((i) => i.original.id);
      } else {
        rows = getRowBy(oldIndex);
        destinationRows = getRowBy(newIndex);

        if (rows?.original.id) {
          jobOrderIds.push(rows.original.id);
        }
      }

      const body: JobOrderReorderPayload = {
        workspaceId: workspace?.id,
        viewId: ViewPps.list.id,
        parentId: destinationRows?.original.parentId
          ? destinationRows?.original?.parentId
          : 0,
        jobOrderIds,
        startingIndex: newIndex,
        atPage: pagination.page,
        atPageSize: pagination.pageSize,
      };

      reorderJobOrder.mutate(body, {
        onSuccess() {
          toastManager.showSuccess(
            "Joborder Reordered Successfully",
            `Your job order ${multipleRowSelections.length === 0 ? rows?.original.jobOrderNo : multipleRowSelections.map((i) => i.original.jobOrderNo).join(", ")} has been Reordered successfully.`,
            3000,
          );
          refetchFn?.();
          setRowSelectionTable({});
        },
        onError(error: any) {
          toastManager.showError(
            "Error Reordered Job Order",
            error?.message?.length > 0
              ? error?.message.join(",")
              : "There was an issue reordering the job order. Please try again or contact support if the problem persists",
          );
        },
      });
    },
    [
      dynamicFilters.length,
      dynamicSorts.length,
      getRowBy,
      multipleRowSelections,
      reorderJobOrder,
      setRowSelectionTable,
      workspace?.id,
    ],
  );

  const onHandleDelete = (rows: any) => {
    const jobOrderNo = rows.map((i: any) => i.original.jobOrderNo);
    const jobOrderIds = rows.map((i: any) => i.original.id);

    mutate(
      { jobOrderIds },
      {
        onSuccess: () => {
          toastManager.showSuccess(
            "Joborder Delete Successfully",
            `Your job order ${jobOrderNo.join(", ")} has been deleted successfully.`,
          );
          refetchFn?.();
          setRowSelectionTable({});
        },
        onError: () => {
          toastManager.showError(
            "Error Delete Job Order",
            "There was an issue deleting the job order. Please try again or contact support if the problem persists",
          );
        },
      },
    );
  };

  const handleEditValue = (value: any, jobId: number, setEditingCell: any) => {
    const body = {
      jobId,
      jobData: value,
    };

    updateJobOrder.mutate(body, {
      onSuccess: () => {
        toastManager.showSuccess(
          "Job Order Updated",
          `Job Order has been successfully updated  .`,
        );
        setEditingCell(null);
        refetchFn?.();
      },
      onError: (error: any) => {
        toastManager.showError(
          "Update Failed",
          error?.message || "Failed to update the job order",
        );
      },
    });
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:gap-2">
        <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
        <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
          Job Orders
        </h2>
      </div>

      {/* WOR-1793 Req #5: Hide editing column if no Plant1_ManageJob */}
      {permissions.canManagePlant1() && (
        <DynamicDataTableEditingColumn
          api={UpdateBulkJobOrder}
          onHandleDelete={onHandleDelete}
          refetch={refetchFn || (() => {})}
          columns={[
            {
              name: "startDate",
              label: "Strtmlfn",
              placeholder: "Enter new Strtmlfn",
              formatDate: "YYYY-MM-DD",
              type: "date",
              icon: Calendar,
            },
            {
              name: "dueDate",
              label: "Shop Edd1",
              placeholder: "Enter new shop",
              formatDate: "YYYY-MM-DD",
              type: "date",
              icon: Calendar,
            },
            {
              name: "technicianIds",
              label: "Technicians",
              placeholder: "technicians",
              type: "multiple-combobox",
              icon: Wrench,
              initialValue: (
                setValue: (name: any, value: any) => void,
                column: ColumnType,
                selection: any,
              ) => {
                setValue(
                  `${column.name}`,
                  selection.original.ppsTechnicians.map((i: any) => i.id) || [],
                );
              },
              options:
                technician?.data?.data?.map((i) => ({
                  value: String(i.id),
                  label: i.name,
                  raw: i,
                })) || [],
            },
            {
              name: "jobOrderNo",
              label: "Job Order No",
              placeholder: "Enter new job order no ",
              type: "input",
              icon: FileText,
            },
            {
              name: "statusId",
              label: "Status",
              placeholder: "Select status ",
              initialValue: (
                setValue: (name: any, value: any) => void,
                column: ColumnType,
                selection: any,
              ) => {
                setValue(
                  `${column.name}`,
                  selection.original.status.statusName || "",
                );
              },
              options: Object.values(jobOrderStatusMap).map(({ id, name }) => ({
                value: String(id),
                label: name,
              })),
              type: "select",
              icon: BarChart,
            },
          ]}
        />
      )}

      {/* New DataTable with all functionality */}
      <DataTable<Job, JobResponse>
        api={JobOrderList}
        columns={columns}
        filterParams={filterParams}
        identifierKey="id"
        subRowKey="subJobOrders"
        onRefetch={handleRefetch}
        onEditValue={handleEditValue}
        onRowOrderChange={handleRowOrderChange}
        onAddSubRow={handleAddSubJobOrder}
        onSelectionChange={handleSelectionChange}
        onTableReady={handleTableReady}
        features={{
          enableSelection: permissions.canManagePlant1(), // WOR-1793 Req #5
          enableRowDragAndDrop: permissions.canManagePlant1(), // WOR-1793 Req #5
          enableColumnDragAndDrop: true,
          enablePinning: true,
          enableResizing: true,
          enableEditing: permissions.canManagePlant1(), // WOR-1793 Req #5
          enableExpanding: true,
          enableSorting: true,
          enablePagination: true,
        }}
        persistence={{
          visibilityKey: "pps-list-column-visibility",
          orderKey: "pps-list-column-order",
          sizingKey: "pps-list-column-sizing",
          pinningKey: "pps-list-column-pinning",
        }}
        actionItems={[
          {
            title: "Copy",
            icon: <ClipboardList className="aero:h-5 aero:w-5" />,
            className: "aero:bg-brand-primary-700",
            hidden: !permissions.canViewPlant1(),
            onClick: (row) => {
              setSelectedItem((prev: any) => ({
                task: row,
                show: !prev.show,
              }));
            },
          },
        ]}
      />

      <KanbanDrawerContent
        open={selectedItem.show}
        onOpenChange={(open) =>
          setSelectedItem((prev: any) => ({
            ...prev,
            show: open,
          }))
        }
        data={selectedItem.task}
        refetch={refetchFn || (() => {})}
        viewMode={!permissions.canManagePlant1()} // WOR-1793 Req #4: View mode if no Plant1_ManageJob
      />
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="aero:sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Action Unavailable</DialogTitle>
            <DialogDescription>
              Reordering is disabled when filters or sorts are applied. Please
              clear them using the Filter and Sort buttons in the toolbar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
